import { inject, injectable } from 'inversify'
import type { FilterStrategyInterface } from '@/services/tables/filters/strategies/types/filterStrategyInterface'
import type { FilterField } from '@/services/tables/filters/fields/filterField'
import { FilterOperationEnum, type FilterOperationValue } from '@/services/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type { BaseFilterStrategy } from '@/services/tables/filters/strategies/baseFilterStrategy'
import type { ParsedFilterType } from '@/services/tables/types'
import type { FilterStrategyGetPopulateDataResult } from '@/services/tables/filters/strategies/types/filterStrategyGetPopulateDataResult'
import type { SelectOption } from '@/services/forms/types'
import { FILTER_RELATION_IS_EMPTY_OPTION_VALUE } from '@/services/tables/filters/types'

@injectable()
export class ArrayWithIsEmptyFilterStrategy implements FilterStrategyInterface {
  constructor(@inject(SERVICE_TYPES.BaseFilterStrategy) protected readonly baseFilterStrategy: BaseFilterStrategy) {}

  public toQueryPart(filter: FilterField): Partial<FilterOperationValue> {
    const value = filter.getApiValue()
    if (this.isRelationIsEmptyValue(value)) {
      return this.baseFilterStrategy.fieldDataToQueryPart({
        operation: FilterOperationEnum.isEmpty,
        value: 1,
      })
    }

    return this.baseFilterStrategy.fieldDataToQueryPart({
      operation: FilterOperationEnum.in,
      relationField: filter.getRelationField(),
      value,
    })
  }

  public getPopulateData(
    filter: FilterField,
    payload: ParsedFilterType[],
    selectedItems?: SelectOption[],
  ): FilterStrategyGetPopulateDataResult {
    const result = this.baseFilterStrategy.getPopulateData(filter, payload, selectedItems)
    if (result && result.operation === FilterOperationEnum.isEmpty) {
      return {
        ...result,
        value: FILTER_RELATION_IS_EMPTY_OPTION_VALUE,
        innerValue: FILTER_RELATION_IS_EMPTY_OPTION_VALUE,
      }
    }
    return result
  }

  public checkFilterValue(filter: FilterField, valueToCheck: unknown): boolean {
    return this.baseFilterStrategy.checkFilterValue(filter, valueToCheck)
  }

  protected isRelationIsEmptyValue(value: unknown): boolean {
    return (
      value === FILTER_RELATION_IS_EMPTY_OPTION_VALUE ||
      (Array.isArray(value) && value.includes(FILTER_RELATION_IS_EMPTY_OPTION_VALUE))
    )
  }
}
